if (!customElements.get('epic-frame-search')) {
  customElements.define('epic-frame-search', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.ef-search-dropdown');
      this.list = this.querySelector('[role="listbox"]');
      this.message = this.querySelector('.ef-search-message');
      this.status = this.querySelector('.ef-search-status');
      this.active = -1;
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', () => this.schedule(), options);
      this.input.addEventListener('focus', () => {
        if (this.list.children.length && this.lastQuery === this.input.value.trim()) this.open();
      }, options);
      this.input.addEventListener('keydown', event => this.keydown(event), options);
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close();
      }, options);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.close();
      }, options);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
      this.events = null;
    }
    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
      this.list.querySelectorAll('[role="option"]').forEach(link => link.setAttribute('aria-selected', 'false'));
    }
    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    schedule() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.close();
      this.list.replaceChildren();
      this.message.hidden = true;
      this.status.textContent = '';
      const query = this.input.value.trim();
      if (!query) return;
      this.timer = setTimeout(() => this.search(query), 250);
    }
    async search(query) {
      this.request = new AbortController();
      const request = this.request;
      try {
        const url = new URL(this.dataset.suggestUrl + '.json', location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (request.signal.aborted || this.input.value.trim() !== query) return;
        const normalize = text => text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
        const words = normalize(query).split(/\s+/).filter(Boolean);
        const products = (data.resources?.results?.products || []).filter(product =>
          words.every(word => normalize(product.title).includes(word)));
        this.list.replaceChildren();
        products.forEach((product, index) => {
          const destination = new URL(product.url, location.origin);
          if (destination.origin !== location.origin || !destination.pathname.includes('/products/')) return;
          const row = document.createElement('li');
          row.setAttribute('role', 'presentation');
          const link = document.createElement('a');
          link.href = destination.href;
          link.id = this.list.id + '-' + index;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');
          link.textContent = product.title;
          row.append(link);
          this.list.append(row);
        });
        this.lastQuery = query;
        const count = this.list.children.length;
        this.message.hidden = count > 0;
        this.message.textContent = this.dataset.empty;
        this.status.textContent = count ? count + ' ' + this.dataset.count : this.dataset.empty;
        if (document.activeElement === this.input) this.open();
      } catch (error) {
        if (error.name === 'AbortError' || this.input.value.trim() !== query) return;
        this.message.textContent = this.dataset.error;
        this.message.hidden = false;
        this.status.textContent = this.dataset.error;
        if (document.activeElement === this.input) this.open();
      }
    }
    keydown(event) {
      if (event.key === 'Escape') {
        this.request?.abort();
        clearTimeout(this.timer);
        this.close();
        return;
      }
      const links = [...this.list.querySelectorAll('[role="option"]')];
      if (!links.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open();
        this.active = event.key === 'ArrowDown'
          ? (this.active + 1) % links.length
          : (this.active - 1 + links.length) % links.length;
        links.forEach((link, index) => link.setAttribute('aria-selected', String(index === this.active)));
        this.input.setAttribute('aria-activedescendant', links[this.active].id);
        links[this.active].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && !this.panel.hidden && this.active >= 0) {
        event.preventDefault();
        links[this.active].click();
      }
    }
  });
}
